// src/utils/batchColor.ts

// 40 very light, mellow pastels
// Only greens, yellows, oranges, and purples – no reds.

// Split by hue family so we can interleave them
const GREENS: string[] = [
  "#E9F7EF",
  "#E4F5EA",
  "#E0F3E5",
  "#DCF1E0",
  "#D7EFD9",
  "#D2ECD4",
  "#E6F6EE",
  "#DAF0E4",
  "#D0E9DB",
  "#CBE5D6",
];

const YELLOWS: string[] = [
  "#FFF9E6",
  "#FFF7DE",
  "#FFF4D6",
  "#FFF2CF",
  "#FFEFCA",
  "#FFF8ED",
  "#FFF3E0",
  "#FCEFD7",
  "#F9EACF",
  "#F6E5C7",
];

const ORANGES: string[] = [
  "#FFEFE5",
  "#FFE9DC",
  "#FFE4D4",
  "#FFDFCD",
  "#FCD9C6",
  "#FBE3D6",
  "#F8DCCD",
  "#F5D6C5",
  "#F2D0BD",
  "#EFCCB7",
];

const PURPLES: string[] = [
  "#F3ECFF",
  "#EEE7FF",
  "#E9E2FF",
  "#E5DDFF",
  "#E1D8FF",
  "#F0E9FF",
  "#EBE3FF",
  "#E6DEFF",
  "#E1D8F8",
  "#DDD3F2",
];

// Deterministic permutation of 0..39 (randomly generated once)
const PERMUTATION_40 = [
  17, 3, 29, 8, 23, 12, 35, 1, 27, 31, 6, 21, 14, 38, 10, 33, 0, 25, 19, 4, 30,
  15, 7, 28, 11, 36, 2, 24, 20, 9, 32, 5, 22, 13, 39, 18, 34, 16, 26, 37,
];

// Interleave by hue so successive indices are very different
// G0, Y0, O0, P0, G1, Y1, O1, P1, ...
export const BASE_PASTEL_COLORS: string[] = [
  GREENS[0],
  YELLOWS[0],
  ORANGES[0],
  PURPLES[0],
  GREENS[1],
  YELLOWS[1],
  ORANGES[1],
  PURPLES[1],
  GREENS[2],
  YELLOWS[2],
  ORANGES[2],
  PURPLES[2],
  GREENS[3],
  YELLOWS[3],
  ORANGES[3],
  PURPLES[3],
  GREENS[4],
  YELLOWS[4],
  ORANGES[4],
  PURPLES[4],
  GREENS[5],
  YELLOWS[5],
  ORANGES[5],
  PURPLES[5],
  GREENS[6],
  YELLOWS[6],
  ORANGES[6],
  PURPLES[6],
  GREENS[7],
  YELLOWS[7],
  ORANGES[7],
  PURPLES[7],
  GREENS[8],
  YELLOWS[8],
  ORANGES[8],
  PURPLES[8],
  GREENS[9],
  YELLOWS[9],
  ORANGES[9],
  PURPLES[9],
];

// Small extra “tint levels” so two batches that collide on the same
// base color still look slightly different.
const VARIANT_LIGHTEN_FACTORS: number[] = [0, 0.08, 0.16];

// Simple deterministic hash for a string
function hashString(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 31 + input.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

// Blend a hex color with white by `factor` (0 = unchanged, 1 = white)
function lightenHex(color: string, factor: number): string {
  if (
    !color ||
    !color.startsWith("#") ||
    (color.length !== 7 && color.length !== 4)
  ) {
    return color;
  }

  let r: number;
  let g: number;
  let b: number;

  if (color.length === 7) {
    r = parseInt(color.slice(1, 3), 16);
    g = parseInt(color.slice(3, 5), 16);
    b = parseInt(color.slice(5, 7), 16);
  } else {
    // #RGB → expand to #RRGGBB
    const rc = color[1];
    const gc = color[2];
    const bc = color[3];
    r = parseInt(rc + rc, 16);
    g = parseInt(gc + gc, 16);
    b = parseInt(bc + bc, 16);
  }

  const mix = (channel: number) =>
    Math.max(0, Math.min(255, Math.round(channel + (255 - channel) * factor)));

  const r2 = mix(r);
  const g2 = mix(g);
  const b2 = mix(b);

  const toHex = (n: number) => n.toString(16).padStart(2, "0");

  return `#${toHex(r2)}${toHex(g2)}${toHex(b2)}`;
}

// Public API used everywhere in Tasks
export function getBatchColor(batchNumber: string | null | undefined): string {
  if (!batchNumber) {
    // No batch → no background color
    return "transparent";
  }

  const hash = hashString(batchNumber);

  // 1) Pick a stable base color index from the 40-color palette
  const unshuffled = hash % BASE_PASTEL_COLORS.length;
  const baseIndex = PERMUTATION_40[unshuffled];
  const baseColor = BASE_PASTEL_COLORS[baseIndex];

  // 2) Pick a small lightening variant so two batches that land on the
  //    same base index still look slightly different.
  const variantIndex =
    Math.floor(hash / BASE_PASTEL_COLORS.length) %
    VARIANT_LIGHTEN_FACTORS.length;

  const factor = VARIANT_LIGHTEN_FACTORS[variantIndex];

  return lightenHex(baseColor, factor);
}
